// One search across local content, the built-in catalog and approved creators.
import React, { useEffect, useMemo, useState } from 'react';
import { FlatList, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useDiet, useHeader } from '../hooks';
import { useAllIngredients, useAllUtensils, useLibrary } from '../store/library';
import { globalScore, GLOBAL_MIN_SCORE } from '../logic/search';
import { COUNTRY_COOKBOOKS, CONTENT_RECIPES } from '../../content';
import Photo from '../components/Photo';
import Icon, { type IconName } from '../components/Icon';
import { useLabels } from '../labels';
import { EmptyState, Field, IconButton, ListItem } from '../components/ui';
import * as social from '../platform/social';
import { useFriends } from '../store/friends';
import { useAccount } from '../store/account';
import type { CreatorProfile } from '../types';
import { font, kindColors, radius, spacing, usePalette } from '../theme';
import type { MediaRef, Recipe } from '../types';
import type { DietFlags } from '../logic/diets';

type Kind = keyof typeof kindColors | 'unit';
const COLORS = { ...kindColors, unit: kindColors.utensil };
type Doc = { key: string; kind: Kind; id: string; title: string; explainer: string; terms: string[]; image?: MediaRef; diets?: DietFlags };

const ICONS: Record<Kind, IconName> = { recipe: 'recipe', mealplan: 'mealplan', cookbook: 'cookbook', ingredient: 'ingredient', utensil: 'utensil', countryCookbook: 'country', unit: 'unit' };
const ROUTES: Record<Kind, 'Recipe' | 'Mealplan' | 'Cookbook' | 'Ingredient' | 'Utensil' | 'CountryCookbook' | 'Unit'> = {
  recipe: 'Recipe', mealplan: 'Mealplan', cookbook: 'Cookbook', ingredient: 'Ingredient', utensil: 'Utensil', countryCookbook: 'CountryCookbook', unit: 'Unit',
};

export default function SearchScreen() {
  const { t } = useTranslation();
  const labels = useLabels();
  const nav = useNav();
  const p = usePalette();
  const lib = useLibrary();
  const ingredients = useAllIngredients();
  const utensils = useAllUtensils();
  const diet = useDiet();
  const [query, setQuery] = useState('');
  const me = useAccount(s => s.user?.id);
  const friends = useFriends(s => s.overview);
  useEffect(() => { if (me) useFriends.getState().refresh().catch(() => {}); }, [me]);
  const friendMatches = friends && friends.me === me && query.trim().length >= 2 ? friends.threads.filter(friend => globalScore(query, friend.friend.displayName, '', []) >= GLOBAL_MIN_SCORE) : [];
  // Global search also finds approved creators, the only public profiles (everyone else stays private).
  const [creators, setCreators] = useState<CreatorProfile[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setCreators([]); return; }
    let active = true;
    setCreators([]);
    const timer = setTimeout(() => {
      social.searchCreators(q).then(found => { if (active) setCreators(found); }, () => { if (active) setCreators([]); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [query]);
  useHeader({
    title: '',
    headerRight: () => <IconButton icon="settings" label={t('settings.title')} onPress={() => nav.navigate('Settings')} />,
  }, [t]);

  const docs = useMemo(() => {
    const terms = (r: Recipe) => [
      ...r.ingredients.flatMap(l => [ingredients[l.ingredientId]?.title ?? '', ...(ingredients[l.ingredientId]?.synonyms ?? [])]),
      ...r.utensils.flatMap(l => [utensils[l.utensilId]?.title ?? '', ...(utensils[l.utensilId]?.synonyms ?? [])]),
      ...r.steps.map(s => s.title),
    ].filter(Boolean);
    const food = (list: Recipe[]) => list.filter(r => !diet.hidden(r));
    const out: Doc[] = [
      ...food(Object.values(lib.recipes)).map(r => ({ key: `r:${r.id}`, kind: 'recipe' as const, id: r.id, title: r.title, explainer: r.explainer, terms: terms(r), image: r.image, diets: diet.issues(r) })),
      ...Object.values(lib.mealplans).map(m => ({ key: `m:${m.id}`, kind: 'mealplan' as const, id: m.id, title: m.title, explainer: m.explainer, terms: [] })),
      ...Object.values(lib.cookbooks).map(c => ({ key: `c:${c.id}`, kind: 'cookbook' as const, id: c.id, title: c.title, explainer: c.explainer, terms: [], image: c.image })),
      ...food(Object.values(CONTENT_RECIPES).filter(r => !lib.recipes[r.id])).map(r => ({ key: `pr:${r.id}`, kind: 'recipe' as const, id: r.id, title: r.title, explainer: r.explainer, terms: terms(r), diets: diet.issues(r) })),
      ...COUNTRY_COOKBOOKS.map(c => ({ key: `cc:${c.id}`, kind: 'countryCookbook' as const, id: c.id, title: c.title, explainer: c.explainer, terms: [c.country] })),
    ];
    // Catalog entities participate for every theme.
    out.push(
        ...Object.values(ingredients).filter(i => !diet.ingredientHidden(i)).map(i => ({ key: `i:${i.id}`, kind: 'ingredient' as const, id: i.id, title: i.title, explainer: i.explainer, terms: i.synonyms })),
        ...Object.values(utensils).map(u => ({ key: `u:${u.id}`, kind: 'utensil' as const, id: u.id, title: u.title, explainer: u.explainer, terms: u.synonyms })),
      ...Object.values(lib.units).map(u => ({ key: `uom:${u.id}`, kind: 'unit' as const, id: u.id, title: u.title, explainer: '', terms: [u.symbol] })),
    );
    return out;
  }, [lib, ingredients, utensils, diet]);

  const results = useMemo(() => {
    if (query.trim().length < 2) return [];
    return docs
      .map(d => ({ d, s: globalScore(query, d.title, d.explainer, d.terms) }))
      .filter(x => x.s >= GLOBAL_MIN_SCORE)
      .sort((a, b) => b.s - a.s || a.d.title.localeCompare(b.d.title))
      .map(x => x.d);
  }, [docs, query]);

  return (
    <View style={{ flex: 1, backgroundColor: p.background }}>
      <View style={{ padding: spacing.l, gap: spacing.m }}>
        <Field placeholder={t('search.prompt')} value={query} onChangeText={setQuery} autoCorrect={false} autoCapitalize="none" accessibilityLabel={t('common.search')} returnKeyType="search" />

      </View>
      <FlatList
        data={results}
        keyExtractor={d => d.key}
        keyboardShouldPersistTaps="handled"
        ListHeaderComponent={creators.length || friendMatches.length ? (
          <View style={{ gap: spacing.s, marginBottom: spacing.m }}>
            {friendMatches.map(friend => <ListItem key={friend.id} title={friend.friend.displayName} icon="buddies" onPress={() => nav.navigate('Tabs', { screen: 'SocialTab', params: { screen: 'SocialHome', params: { friendId: friend.friend.userId } } } as never)} />)}
            {creators.map(c => (
              <ListItem key={c.handle} title={c.displayName} subtitle={`@${c.handle}`} icon="sparkles" onPress={() => nav.navigate('Creator', { handle: c.handle })} />
            ))}
          </View>
        ) : undefined}
        contentContainerStyle={{ paddingHorizontal: spacing.l, gap: spacing.s, paddingBottom: 120 }}
        ListEmptyComponent={
          creators.length || friendMatches.length || query.trim().length < 2 ? undefined : (
            <EmptyState icon="search" title={t('search.noResults', { query })} />
          )
        }
        renderItem={({ item }) => (
          <ListItem
            title={item.title}
            subtitle={labels ? item.explainer : undefined}
            diets={item.diets}
            leading={<Photo media={item.image} seed={item.id} icon={ICONS[item.kind]} style={{ width: 44, height: 44 }} iconSize={20} />}
            right={
              labels ? (
                <Text style={{ fontSize: font.caption, fontWeight: '700', color: COLORS[item.kind], backgroundColor: `${COLORS[item.kind]}22`, paddingHorizontal: spacing.s, paddingVertical: 2, borderRadius: radius.pill, overflow: 'hidden' }}>
                  {item.kind === 'countryCookbook' ? t('kinds.countryCookbook') : t(`kinds.${item.kind}`)}
                </Text>
              ) : (
                <View accessibilityLabel={item.kind === 'countryCookbook' ? t('kinds.countryCookbook') : t(`kinds.${item.kind}`)} style={{ backgroundColor: `${COLORS[item.kind]}22`, borderRadius: radius.pill, padding: 6 }}>
                  <Icon name={ICONS[item.kind]} size={16} color={COLORS[item.kind]} />
                </View>
              )
            }
            onPress={() => nav.navigate(ROUTES[item.kind], { id: item.id })}
          />
        )}
      />
    </View>
  );
}
